'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Grid from '@mui/material/Grid';
import Link from 'next/link';
import { ensureProtocol } from '../../lib/ensureProtocol';

interface Bot {
  slug: string;
  name: string;
  description: string | null;
}

interface LandingContentProps {
  locale: string;
  featuredBots: Bot[];
}

export function LandingContent({ locale, featuredBots }: LandingContentProps) {
  const t = useTranslations('consumer.landing');
  const creatorUrl = ensureProtocol(process.env.NEXT_PUBLIC_CREATOR_URL || 'https://boty.app');

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3,
        }}
      >
        <Typography variant="h3" component="h1" fontWeight={700}>
          {t('title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 400 }}>
          {t('subtitle')}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {t('description')}
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('createOwn')}
          </Typography>
          <Button
            LinkComponent={Link}
            href={creatorUrl}
            variant="contained"
            size="large"
          >
            {t('createCta')}
          </Button>
        </Box>
      </Box>

      {/* Featured Bots Section */}
      {featuredBots.length > 0 && (
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h5"
            component="h2"
            fontWeight={600}
            textAlign="center"
            gutterBottom
          >
            {t('featuredBots')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 3 }}
          >
            {t('featuredBotsSubtitle')}
          </Typography>

          <Grid container spacing={2}>
            {featuredBots.map((bot) => (
              <Grid key={bot.slug} size={{ xs: 12, sm: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 3 },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {bot.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {bot.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      LinkComponent={Link}
                      href={`/${locale}/${bot.slug}`}
                      variant="outlined"
                      size="small"
                      fullWidth
                    >
                      {t('tryIt')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              LinkComponent={Link}
              href={`/${locale}/samples`}
              variant="text"
              size="small"
            >
              {t('seeAllSamples')}
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
}
